import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  AttachMoney,
  Calculate,
  Edit,
  Save,
  Print,
  EmojiEvents,
  CheckCircle
} from '@mui/icons-material';

const PAYOUT_STRUCTURES = {
  TOP_3: { name: 'Top 3', places: 3, split: [50, 30, 20] },
  TOP_5: { name: 'Top 5', places: 5, split: [40, 25, 20, 10, 5] },
  TOP_10: { name: 'Top 10', places: 10, split: [30, 20, 15, 10, 8, 6, 4, 3, 2, 2] },
  WINNER_TAKES_ALL: { name: 'Winner Takes All', places: 1, split: [100] },
  CUSTOM: { name: 'Custom', places: 0, split: [] }
};

export default function PayoutManagement() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prizePool, setPrizePool] = useState(0);
  const [selectedStructure, setSelectedStructure] = useState('TOP_3');
  const [customSplits, setCustomSplits] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [payoutsSaved, setPayoutsSaved] = useState(false);

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  useEffect(() => {
    calculatePayouts();
  }, [prizePool, selectedStructure, customSplits, leaderboard]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      // Load saved payout configuration
      if (tournamentData.payoutConfig) {
        setPrizePool(tournamentData.payoutConfig.prizePool || 0);
        setSelectedStructure(tournamentData.payoutConfig.structure || 'TOP_3');
        setCustomSplits(tournamentData.payoutConfig.customSplits || []);
      } else {
        // Calculate prize pool from entry fees
        setPrizePool(tournamentData.entryFee * (tournamentData.players?.length || 0));
      }

      // Load saved payouts if any
      if (tournamentData.payouts) {
        setPayouts(tournamentData.payouts);
        setPayoutsSaved(true);
      }

      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

      const leaderboardData = calculateLeaderboard(tournamentData, playersData);
      setLeaderboard(leaderboardData);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateLeaderboard = (tournament, players) => {
    const scores = tournament.scores || {};
    const tournamentType = tournament.type || 'STROKEPLAY_SCRATCH';

    const playersWithScores = players
      .filter(player => scores[player.id])
      .map(player => {
        const playerScores = scores[player.id];
        const grossTotal = Object.values(playerScores).reduce((sum, score) => 
          sum + (parseInt(score) || 0), 0);
        
        let netTotal = grossTotal;
        if (tournamentType === 'STROKEPLAY_HANDICAPPED' && player.handicap) {
          netTotal = grossTotal - Math.floor(player.handicap);
        }

        return {
          id: player.id,
          name: `${player.firstName} ${player.lastName}`,
          handicap: player.handicap || 0,
          grossTotal,
          netTotal,
          scoringScore: tournamentType === 'STROKEPLAY_SCRATCH' ? grossTotal : netTotal
        };
      });

    playersWithScores.sort((a, b) => a.scoringScore - b.scoringScore);

    return playersWithScores.map((player, index) => ({
      ...player,
      position: index + 1
    }));
  };

  const calculatePayouts = () => {
    if (!prizePool || !leaderboard.length) {
      setPayouts([]);
      return;
    }

    const structure = PAYOUT_STRUCTURES[selectedStructure];
    let splits = structure.split;

    if (selectedStructure === 'CUSTOM' && customSplits.length > 0) {
      splits = customSplits;
    }

    const calculatedPayouts = splits.map((percentage, index) => {
      const player = leaderboard[index];
      if (!player) return null;

      return {
        position: index + 1,
        playerId: player.id,
        playerName: player.name,
        percentage,
        amount: Math.round((prizePool * percentage / 100) * 100) / 100,
        paid: false
      };
    }).filter(p => p !== null);

    setPayouts(calculatedPayouts);
  };

  const handleSavePayouts = async () => {
    try {
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        payouts,
        payoutConfig: {
          prizePool,
          structure: selectedStructure,
          customSplits,
          calculatedAt: new Date()
        }
      });

      setPayoutsSaved(true);
      alert('Payouts saved successfully!');

    } catch (error) {
      console.error('Error saving payouts:', error);
      alert('Error saving payouts: ' + error.message);
    }
  };

  const handleMarkPaid = async (payoutIndex) => {
    try {
      const updatedPayouts = [...payouts];
      updatedPayouts[payoutIndex].paid = !updatedPayouts[payoutIndex].paid;
      updatedPayouts[payoutIndex].paidAt = updatedPayouts[payoutIndex].paid ? new Date() : null;

      await updateDoc(doc(db, 'tournaments', tournamentId), {
        payouts: updatedPayouts
      });

      setPayouts(updatedPayouts);

    } catch (error) {
      console.error('Error updating payout status:', error);
      alert('Error updating payout status: ' + error.message);
    }
  };

  const handlePrintPayouts = () => {
    window.print();
  };

  const totalPayoutAmount = payouts.reduce((sum, p) => sum + p.amount, 0);
  const totalPaid = payouts.filter(p => p.paid).length;

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(`/tournaments/${tournamentId}/director`)}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">Payout Management</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {tournament.name}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrintPayouts}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSavePayouts}
            disabled={payouts.length === 0}
          >
            Save Payouts
          </Button>
        </Box>
      </Box>

      {payoutsSaved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payouts have been saved. {totalPaid} of {payouts.length} payouts marked as paid.
        </Alert>
      )}

      {/* Prize Pool Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Prize Pool Configuration</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Prize Pool"
              type="number"
              fullWidth
              value={prizePool}
              onChange={(e) => setPrizePool(parseFloat(e.target.value) || 0)}
              InputProps={{
                startAdornment: <AttachMoney />
              }}
              helperText={`Entry Fee: $${tournament.entryFee || 0} × ${leaderboard.length} players = $${(tournament.entryFee || 0) * leaderboard.length}`}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Payout Structure</InputLabel>
              <Select
                value={selectedStructure}
                label="Payout Structure"
                onChange={(e) => setSelectedStructure(e.target.value)}
              >
                {Object.entries(PAYOUT_STRUCTURES).map(([key, structure]) => (
                  <MenuItem key={key} value={key}>
                    {structure.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {selectedStructure === 'CUSTOM' && (
          <Box mt={3}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Enter custom percentage splits. Total must equal 100%.
            </Alert>
            <Button
              variant="outlined"
              onClick={() => setOpenEditDialog(true)}
            >
              Edit Custom Splits
            </Button>
          </Box>
        )}
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AttachMoney sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5">${prizePool.toFixed(2)}</Typography>
              <Typography color="text.secondary">Prize Pool</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <EmojiEvents sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h5">{payouts.length}</Typography>
              <Typography color="text.secondary">Paying Places</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Calculate sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h5">${totalPayoutAmount.toFixed(2)}</Typography>
              <Typography color="text.secondary">Total Payouts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5">{totalPaid} / {payouts.length}</Typography>
              <Typography color="text.secondary">Paid Out</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payout Breakdown */}
      <Paper>
        <Box p={2} bgcolor="primary.light">
          <Typography variant="h6" color="primary.contrastText">
            Payout Breakdown
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center"><strong>Position</strong></TableCell>
                <TableCell><strong>Player</strong></TableCell>
                <TableCell align="center"><strong>Score</strong></TableCell>
                <TableCell align="right"><strong>Percentage</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      Configure prize pool and payout structure to calculate payouts
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((payout, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      backgroundColor: index === 0 ? 'rgba(255, 215, 0, 0.1)' : 'inherit'
                    }}
                  >
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        {index === 0 && <EmojiEvents sx={{ color: 'gold', fontSize: 20 }} />}
                        <Typography variant="h6" fontWeight={index < 3 ? 'bold' : 'normal'}>
                          {payout.position}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {payout.playerName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {leaderboard[index]?.scoringScore || '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography>{payout.percentage}%</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6" color="success.main">
                        ${payout.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {payout.paid ? (
                        <Chip 
                          label="Paid" 
                          color="success" 
                          size="small"
                          icon={<CheckCircle />}
                        />
                      ) : (
                        <Chip 
                          label="Pending" 
                          color="default" 
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant={payout.paid ? 'outlined' : 'contained'}
                        color={payout.paid ? 'default' : 'success'}
                        onClick={() => handleMarkPaid(index)}
                      >
                        {payout.paid ? 'Undo' : 'Mark Paid'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {payouts.length > 0 && (
          <Box p={2} bgcolor="grey.100">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Prize Pool:</strong> ${prizePool.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Allocated:</strong> ${totalPayoutAmount.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Remaining:</strong> ${(prizePool - totalPayoutAmount).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Payouts Made:</strong> {totalPaid} of {payouts.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Amount Paid:</strong> ${payouts.filter(p => p.paid).reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Amount Pending:</strong> ${payouts.filter(p => !p.paid).reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Custom Splits Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Custom Payout Splits</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter percentage splits for each position. Total must equal 100%.
          </Alert>
          <List>
            {[...Array(10)].map((_, index) => (
              <ListItem key={index}>
                <ListItemText primary={`Position ${index + 1}`} />
                <TextField
                  type="number"
                  size="small"
                  sx={{ width: 100 }}
                  value={customSplits[index] || ''}
                  onChange={(e) => {
                    const newSplits = [...customSplits];
                    newSplits[index] = parseFloat(e.target.value) || 0;
                    setCustomSplits(newSplits);
                  }}
                  InputProps={{
                    endAdornment: <Typography>%</Typography>
                  }}
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">
            Total: {customSplits.reduce((sum, val) => sum + (val || 0), 0)}%
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}









